import React from 'react';
import BaseModal, {
	BaseModalProps,
} from '../../../../../Base/Components/BaseModal/BaseModal';
import {
	TermItem,
	useUpdateWorkplaceMedicalProcedureMutation,
	WorkplaceMedicalProceduresQuery,
} from '../../../../../../graphql/graphql';
import Loading from '../../../../../../Components/Loading';
import useApiClient from '../../../../../Base/Hooks/useApiClient';
import ManagerInsuranceCompaniesCheckboxes from '../ManagerInsuranceCompaniesCheckboxes/ManagerInsuranceCompaniesCheckboxes';
import { formatInsuranceCompaniesOptions } from '../../../../Helpers/InsuranceCompanies/formatInsuranceCompaniesOptions';
import useInsuranceCompanies from '../../../../Hooks/useInsuranceCompanies';
import useNotification from '../../../../../../Hooks/useNotification.hook';

type WorkplaceMedicalProcedure = NonNullable<
	NonNullable<
		WorkplaceMedicalProceduresQuery['workplaceMedicalProcedures']
	>['data']
>[number];

interface Props extends BaseModalProps {
	item: WorkplaceMedicalProcedure;
	reloadData: () => void;
}

const CODE_KEY = 'value2';

const ManagerInsuranceCompaniesEditModal: React.FC<Props> = ({
	item,
	reloadData,
	...modalProps
}) => {
	const { displayGQLError } = useApiClient();
	const { data, isLoading } = useInsuranceCompanies();
	const { showNotificationSuccess } = useNotification();

	const [updateWorkplaceMedicalProcedure] =
		useUpdateWorkplaceMedicalProcedureMutation();

	if (isLoading) {
		return <Loading />;
	}

	const insuranceCompanies = data?.termItems?.data || [];
	const insuranceCompaniesData = insuranceCompanies.filter(
		(item): item is TermItem => item !== null,
	);
	const modalItems = formatInsuranceCompaniesOptions(insuranceCompaniesData);

	const selectedItemsIds = insuranceCompanies
		.map(company => company[CODE_KEY])
		.filter(
			(code): code is string =>
				typeof code === 'string' &&
				(item.insuranceCompanyCodes || []).includes(code),
		);

	const handleSubmit = async (data: Record<string, boolean>) => {
		const insuranceCompanyCodes = insuranceCompanies
			.filter(
				insuranceCompany =>
					insuranceCompany[CODE_KEY] && data[insuranceCompany[CODE_KEY]],
			)
			.map(insuranceCompany => insuranceCompany?.[CODE_KEY])
			.filter((code): code is string => Boolean(code));

		const result = await updateWorkplaceMedicalProcedure({
			id: item.id,
			data: { insuranceCompanyCodes },
		});

		displayGQLError(result);

		if ('data' in result) {
			showNotificationSuccess({
				content: 'component.caseType.chooseInsuranceCompaniesSuccess',
			});
			modalProps.onClose();
			reloadData();
		}
	};

	return (
		<BaseModal
			{...modalProps}
			title='component.caseType.chooseInsuranceCompanies'>
			<ManagerInsuranceCompaniesCheckboxes
				items={modalItems}
				selectedItems={selectedItemsIds}
				onSubmit={handleSubmit}
			/>
		</BaseModal>
	);
};

export default ManagerInsuranceCompaniesEditModal;
